import type { FunctionComponent } from "react"

import { clsx } from "clsx"
import Image from "next/image"

import { Divider } from "@jobteaser/spark/components/Divider"
import { Calendar } from "@jobteaser/spark/components/Icons/Calendar"
import { HeartOutlined } from "@jobteaser/spark/components/Icons/HeartOutlined"
import { Home } from "@jobteaser/spark/components/Icons/Home"
import { List } from "@jobteaser/spark/components/Icons/List"
import { LockOutlined } from "@jobteaser/spark/components/Icons/LockOutlined"
import { Logout } from "@jobteaser/spark/components/Icons/Logout"
import { Refresh } from "@jobteaser/spark/components/Icons/Refresh"
import { Search } from "@jobteaser/spark/components/Icons/Search"
import { Send } from "@jobteaser/spark/components/Icons/Send"
import { SettingsOutlined } from "@jobteaser/spark/components/Icons/SettingsOutlined"
import { UserCircle } from "@jobteaser/spark/components/Icons/UserCircle"
import { UsersGroup } from "@jobteaser/spark/components/Icons/UsersGroup"
import { Text } from "@jobteaser/spark/components/Text"

import { useCareerCenterFetch } from "@/modules/careerCenter/hooks/useCareerCenterFetch"
import { useCompanyFetch } from "@/modules/companies/client/hooks/useCompanyFetch"
// import { useGetCsSpotlightPage } from "@/modules/csSpotlight/hooks/useGetCsSpotlightPage"
import { UserAvatar } from "@/modules/dashboard/components/UserAvatar/UserAvatar"
import { isLinkActive } from "@/modules/headerFooterFO/Header/helpers/isLinkActive"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { useLocale } from "@/modules/locales/useLocale"
import { useProfileFetch } from "@/modules/profile/client/hooks/useProfileFetch"
import { Link } from "@/modules/routing/components/Link"
import { useHref } from "@/modules/routing/hooks/useHref"
import { useCurrentUserFetch } from "@/modules/user/client/hooks/useCurrentUserFetch"

import dropdownMenuStyles from "@/modules/headerFooterFO/Header/styles/DropdownMenu.module.css"

export const DropdownUser: FunctionComponent = () => {
  const { t } = useTranslation(["shared_header"])
  const href = useHref()
  const locale = useLocale()
  const { dataCurrentUser } = useCurrentUserFetch()
  const { dataProfile } = useProfileFetch()
  const { dataCompany } = useCompanyFetch(dataCurrentUser?.attributes?.companyPermalink || undefined)
  const { dataCareerCenter } = useCareerCenterFetch()

  /* Waiting for the Dashboard release */
  // const { page: csSpotlightPage } = useGetCsSpotlightPage()

  if (!dataCurrentUser) return null

  const {
    anyCompanyRole,
    companyPermalink,
    hasSchoolProfile,
    isAdmin,
    isHiddenAdmin,
    isStudent,
    isWithCompanySpace,
    uuid,
  } = dataCurrentUser.attributes

  const isMyAppointmentsVisible = dataCareerCenter?.careerCenterConf?.modules.hasAppointments

  const userAvatar = dataProfile?.pictureUri ?? undefined
  /* Waiting for the Dashboard release */
  // const CCLogo = dataCareerCenter?.logo
  const companyLogo = dataCompany?.page?.logoUrl

  return (
    <>
      <button
        type="button"
        className={clsx(dropdownMenuStyles["hdr-DropdownMenu-link"], {
          [dropdownMenuStyles["hdr-DropdownMenu-link--active"]]: isLinkActive(href, `/${locale}/profiles/${uuid}`),
        })}
        aria-label={t("shared_header.dropdown_my_account") as string}
        data-testid="dropdown-user-button-avatar"
      >
        <UserAvatar src={userAvatar} />
      </button>
      <ul className={dropdownMenuStyles["hdr-DropdownMenu-submenu"]}>
        {!!isHiddenAdmin && (
          <li className={dropdownMenuStyles["hdr-DropdownMenu-submenuItem"]}>
            <a
              className={clsx(
                dropdownMenuStyles["hdr-DropdownMenu-submenuLink"],
                dropdownMenuStyles["hdr-DropdownMenu-submenuLink--withIcon"]
              )}
              href={`/${locale}/backend/disconnect_as`}
              data-testid="dropdown-user-link-disconnect-as"
            >
              <Refresh />
              <Text variant="body2">{t("shared_header.nav_my_account")}</Text>
            </a>
          </li>
        )}

        {/* Student */}
        {!!isStudent && uuid && (
          <>
            {/* Student - Admin CC: My Profile */}
            <li className={dropdownMenuStyles["hdr-DropdownMenu-submenuItem"]}>
              <a
                className={clsx(
                  dropdownMenuStyles["hdr-DropdownMenu-submenuLink"],
                  dropdownMenuStyles["hdr-DropdownMenu-submenuLink--withIcon"],
                  {
                    [dropdownMenuStyles["hdr-DropdownMenu-submenuLink--active"]]: isLinkActive(
                      href,
                      `/${locale}/profiles/${uuid}`,
                      true
                    ),
                  }
                )}
                href={`/${locale}/profiles/${uuid}`}
                data-testid="dropdown-user-link-profile"
              >
                <UserCircle />
                <Text variant="body2">{t("shared_header.nav_my_profile")}</Text>
              </a>
            </li>
            <li className={dropdownMenuStyles["hdr-DropdownMenu-divider"]} aria-hidden>
              <Divider />
            </li>

            {/* Student - Admin CC: Dashboard */}
            <li className={dropdownMenuStyles["hdr-DropdownMenu-submenuItem"]}>
              <Link
                className={clsx(
                  dropdownMenuStyles["hdr-DropdownMenu-submenuLink"],
                  dropdownMenuStyles["hdr-DropdownMenu-submenuLink--withIcon"],
                  {
                    [dropdownMenuStyles["hdr-DropdownMenu-submenuLink--active"]]: isLinkActive(
                      href,
                      `/${locale}/dashboard`,
                      true
                    ),
                  }
                )}
                href={`/${locale}/dashboard`}
                data-testid="dropdown-user-link-dashboard"
              >
                <Home />
                <Text variant="body2">{t("shared_header.nav_my_dashboard")}</Text>
              </Link>
            </li>

            {/* Student - Admin CC: My Search Criteria */}
            <li className={dropdownMenuStyles["hdr-DropdownMenu-submenuItem"]}>
              <a
                className={clsx(
                  dropdownMenuStyles["hdr-DropdownMenu-submenuLink"],
                  dropdownMenuStyles["hdr-DropdownMenu-submenuLink--withIcon"],
                  {
                    [dropdownMenuStyles["hdr-DropdownMenu-submenuLink--active"]]: isLinkActive(
                      href,
                      `/${locale}/dashboard/edit`,
                      true
                    ),
                  }
                )}
                href={`/${locale}/dashboard/edit`}
                data-testid="dropdown-user-link-search-criteria"
              >
                <List />
                <Text variant="body2">{t("shared_header.nav_my_search_criteria")}</Text>
              </a>
            </li>

            {/* Student - Admin CC: My Saved Search */}
            <li className={dropdownMenuStyles["hdr-DropdownMenu-submenuItem"]}>
              <Link
                className={clsx(
                  dropdownMenuStyles["hdr-DropdownMenu-submenuLink"],
                  dropdownMenuStyles["hdr-DropdownMenu-submenuLink--withIcon"],
                  {
                    [dropdownMenuStyles["hdr-DropdownMenu-submenuLink--active"]]: isLinkActive(
                      href,
                      `/${locale}/dashboard/my_saved_search`,
                      true
                    ),
                  }
                )}
                href={`/${locale}/dashboard/my_saved_search`}
                data-testid="dropdown-user-link-saved-search"
              >
                <Search />
                <Text variant="body2">{t("shared_header.nav_my_saved_search")}</Text>
              </Link>
            </li>

            {/* Student - Admin CC: My Job Offers */}
            <li className={dropdownMenuStyles["hdr-DropdownMenu-submenuItem"]}>
              <a
                className={clsx(
                  dropdownMenuStyles["hdr-DropdownMenu-submenuLink"],
                  dropdownMenuStyles["hdr-DropdownMenu-submenuLink--withIcon"],
                  {
                    [dropdownMenuStyles["hdr-DropdownMenu-submenuLink--active"]]: isLinkActive(
                      href,
                      `/${locale}/dashboard/my_job_offers`,
                      true
                    ),
                  }
                )}
                href={`/${locale}/dashboard/my_job_offers`}
                data-testid="dropdown-user-link-job-offers"
              >
                <HeartOutlined />
                <Text variant="body2">{t("shared_header.nav_my_saved_jobs")}</Text>
              </a>
            </li>

            {/* Student - Admin CC: My Applications */}
            <li className={dropdownMenuStyles["hdr-DropdownMenu-submenuItem"]}>
              <a
                className={clsx(
                  dropdownMenuStyles["hdr-DropdownMenu-submenuLink"],
                  dropdownMenuStyles["hdr-DropdownMenu-submenuLink--withIcon"],
                  {
                    [dropdownMenuStyles["hdr-DropdownMenu-submenuLink--active"]]: isLinkActive(
                      href,
                      `/${locale}/dashboard/my_applications`,
                      true
                    ),
                  }
                )}
                href={`/${locale}/dashboard/my_applications`}
                data-testid="dropdown-user-link-applications"
              >
                <Send />
                <Text variant="body2">{t("shared_header.nav_my_applications")}</Text>
              </a>
            </li>

            {/* Student - Admin CC: My Events */}
            <li className={dropdownMenuStyles["hdr-DropdownMenu-submenuItem"]}>
              <a
                className={clsx(
                  dropdownMenuStyles["hdr-DropdownMenu-submenuLink"],
                  dropdownMenuStyles["hdr-DropdownMenu-submenuLink--withIcon"],
                  {
                    [dropdownMenuStyles["hdr-DropdownMenu-submenuLink--active"]]: isLinkActive(
                      href,
                      `/${locale}/dashboard/my_events`,
                      true
                    ),
                  }
                )}
                href={`/${locale}/dashboard/my_events`}
                data-testid="dropdown-user-link-events"
              >
                <Calendar />
                <Text variant="body2">{t("shared_header.nav_my_events")}</Text>
              </a>
            </li>

            {/* Student - Admin CC: My appointments */}
            {isMyAppointmentsVisible && (
              <li className={dropdownMenuStyles["hdr-DropdownMenu-submenuItem"]}>
                <a
                  className={clsx(
                    dropdownMenuStyles["hdr-DropdownMenu-submenuLink"],
                    dropdownMenuStyles["hdr-DropdownMenu-submenuLink--withIcon"],
                    {
                      [dropdownMenuStyles["hdr-DropdownMenu-submenuLink--active"]]: isLinkActive(
                        href,
                        `/${locale}/dashboard/my_appointments`,
                        true
                      ),
                    }
                  )}
                  href={`/${locale}/dashboard/my_appointments`}
                  data-testid="dropdown-user-link-appointments"
                >
                  <UsersGroup />
                  <Text variant="body2">{t("shared_header.nav_my_appointments")}</Text>
                </a>
              </li>
            )}
            <li className={dropdownMenuStyles["hdr-DropdownMenu-divider"]} aria-hidden>
              <Divider />
            </li>

            {/* Student - Admin CC: My Career Service */}
            {/* Do not display the career service space link for CC without space */}
            {/* Waiting for the Dashboard release */}
            {/* {!!careerCenterUuid && !!csSpotlightPage && (
              <>
                <li className={dropdownMenuStyles["hdr-DropdownMenu-submenuItem"]}>
                  <Link
                    className={clsx(
                      dropdownMenuStyles["hdr-DropdownMenu-submenuLink"],
                      dropdownMenuStyles["hdr-DropdownMenu-submenuLink--withIcon"],
                      {
                        [dropdownMenuStyles["hdr-DropdownMenu-submenuLink--active"]]: isLinkActive(
                          href,
                          `/${locale}/career-service`,
                          true
                        ),
                      }
                    )}
                    href={`/${locale}/career-service`}
                    data-testid="dropdown-user-link-career-service"
                  >
                    {!!CCLogo && (
                      <Image
                        src={CCLogo}
                        width={20}
                        height={20}
                        alt=""
                        className={dropdownMenuStyles["hdr-DropdownMenu-submenuLogo"]}
                      />
                    )}
                    <Text variant="body2">{t("shared_header.nav_my_career_service")}</Text>
                  </Link>
                </li>
                <li className={dropdownMenuStyles["hdr-DropdownMenu-divider"]} aria-hidden>
                  <Divider />
                </li>
              </>
            )} */}

            {/* JT Admin */}
            {!!isAdmin && (
              <li className={dropdownMenuStyles["hdr-DropdownMenu-submenuItem"]}>
                <a
                  className={clsx(
                    dropdownMenuStyles["hdr-DropdownMenu-submenuLink"],
                    dropdownMenuStyles["hdr-DropdownMenu-submenuLink--withIcon"]
                  )}
                  href={`/${locale}/backend`}
                  data-testid="dropdown-user-link-administration"
                >
                  <Home />
                  <Text variant="body2">{t("shared_header.nav_admin_space")}</Text>
                </a>
              </li>
            )}

            {/* Admin CC: Administration */}
            {!!hasSchoolProfile && (
              <li className={dropdownMenuStyles["hdr-DropdownMenu-submenuItem"]}>
                <a
                  className={clsx(
                    dropdownMenuStyles["hdr-DropdownMenu-submenuLink"],
                    dropdownMenuStyles["hdr-DropdownMenu-submenuLink--withIcon"]
                  )}
                  href={`/${locale}/backend`}
                  data-testid="dropdown-user-link-cc-administration"
                >
                  <Home />
                  <Text variant="body2">{t("shared_header.nav_manage_my_space")}</Text>
                </a>
              </li>
            )}

            {/* Student - Admin CC: Privacy */}
            <li className={dropdownMenuStyles["hdr-DropdownMenu-submenuItem"]}>
              <a
                className={clsx(
                  dropdownMenuStyles["hdr-DropdownMenu-submenuLink"],
                  dropdownMenuStyles["hdr-DropdownMenu-submenuLink--withIcon"],
                  {
                    [dropdownMenuStyles["hdr-DropdownMenu-submenuLink--active"]]: isLinkActive(
                      href,
                      `/${locale}/profiles/consent`,
                      true
                    ),
                  }
                )}
                href={`/${locale}/profiles/consent`}
                data-testid="dropdown-user-link-privacy"
              >
                <LockOutlined />
                <Text variant="body2">{t("shared_header.nav_consent_section_link_title")}</Text>
              </a>
            </li>

            {/* Student - Admin CC: Account Settings */}
            <li className={dropdownMenuStyles["hdr-DropdownMenu-submenuItem"]}>
              <a
                className={clsx(
                  dropdownMenuStyles["hdr-DropdownMenu-submenuLink"],
                  dropdownMenuStyles["hdr-DropdownMenu-submenuLink--withIcon"],
                  {
                    [dropdownMenuStyles["hdr-DropdownMenu-submenuLink--active"]]: isLinkActive(
                      href,
                      `/${locale}/profiles/${uuid}/account_settings/edit`,
                      true
                    ),
                  }
                )}
                href={`/${locale}/profiles/${uuid}/account_settings/edit`}
                data-testid="dropdown-user-link-account-settings"
              >
                <SettingsOutlined />
                <Text variant="body2">{t("shared_header.nav_account_settings")}</Text>
              </a>
            </li>
          </>
        )}

        {/* Company */}
        {!!anyCompanyRole && (
          <>
            {/* Do not display the company space link for companies without space */}
            {isWithCompanySpace && companyPermalink && (
              <>
                <li className={dropdownMenuStyles["hdr-DropdownMenu-submenuItem"]}>
                  <Link
                    className={clsx(
                      dropdownMenuStyles["hdr-DropdownMenu-submenuLink"],
                      dropdownMenuStyles["hdr-DropdownMenu-submenuLink--withIcon"],
                      {
                        [dropdownMenuStyles["hdr-DropdownMenu-submenuLink--active"]]: isLinkActive(
                          href,
                          `/${locale}/companies/${companyPermalink}`,
                          true
                        ),
                      }
                    )}
                    href={`/${locale}/companies/${companyPermalink}`}
                    data-testid="dropdown-user-link-company-space"
                  >
                    {!!companyLogo && (
                      <Image
                        src={companyLogo}
                        width={20}
                        height={20}
                        alt=""
                        className={dropdownMenuStyles["hdr-DropdownMenu-submenuLogo"]}
                      />
                    )}
                    <Text variant="body2">{t("shared_header.nav_my_space")}</Text>
                  </Link>
                </li>
                <li className={dropdownMenuStyles["hdr-DropdownMenu-divider"]} aria-hidden>
                  <Divider />
                </li>
              </>
            )}
            <li className={dropdownMenuStyles["hdr-DropdownMenu-submenuItem"]}>
              <a
                className={clsx(
                  dropdownMenuStyles["hdr-DropdownMenu-submenuLink"],
                  dropdownMenuStyles["hdr-DropdownMenu-submenuLink--withIcon"]
                )}
                href={`/${locale}/backend`}
                data-testid="dropdown-user-link-company-administration"
              >
                <Home />
                <Text variant="body2">{t("shared_header.nav_manage_my_space")}</Text>
              </a>
            </li>
          </>
        )}

        {/* All users: Disconnect */}
        <li className={dropdownMenuStyles["hdr-DropdownMenu-submenuItem"]}>
          <a
            className={clsx(
              dropdownMenuStyles["hdr-DropdownMenu-submenuLink"],
              dropdownMenuStyles["hdr-DropdownMenu-submenuLink--withIcon"]
            )}
            href={`/${locale}/users/sign_out`}
            data-testid="dropdown-user-link-disconnect"
          >
            <Logout />
            <Text variant="body2">{t("shared_header.nav_disconnect")}</Text>
          </a>
        </li>
      </ul>
    </>
  )
}
